import { useState } from 'react'
import { formatDateTime } from 'utils/util'
import { type Sweepstake } from 'types/api'
import { useSnackbar } from 'stores/useSnackbar'
import { type RequiredId } from 'utils/typeUtils'
import { Box, Grid, Typography } from '@mui/material'
import { EditLocationSweepstakeDetailsModal } from './EditLocationSweepstakeDetailsModal'
import { SectionHeader } from 'components/SectionHeader'
import { usePutLocationSweepstake } from 'hooks/api/usePutLocationSweepstake'
import { usePutLocationSweepstakeGroup } from 'hooks/api/usePutLocationSweepstakeGroup'
import { useNavigate } from 'react-router-dom'

export interface EditSweepstakeDetailsProps {
  sweepstake: RequiredId<Sweepstake>
  onSubmitSweepstake: (sweepstake: RequiredId<Sweepstake>) => void
  toggleModalIsOpen: () => void
  updateIsActive: boolean
  isModalOpen: boolean
}

export const LocationSweepstakesDetailsSection = ({
  sweepstake,
}: {
  sweepstake: Sweepstake
}) => {
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()

  const toggleModalIsOpen = () => setIsModalOpen(!isModalOpen)

  const putLocationSweepstake = usePutLocationSweepstake({
    ...sweepstake,
    onSuccess: (data) => {
      setMessage('Sweepstake has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      toggleModalIsOpen()
      navigate(`/Contests/LocationSweepstakes/${data.id}/Overview`)
    },
    onError: () => {
      setMessage('Error updating Sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const putLocationSweepstakeGroup = usePutLocationSweepstakeGroup({
    ...sweepstake,
    onSuccess: (data) => {
      setMessage('Sweepstake has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      toggleModalIsOpen()
      navigate(`/Contests/LocationSweepstakes/${data.id}/Overview`)
    },
    onError: () => {
      setMessage('Error updating Sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  return (
    <>
      <SectionHeader
        title="Sweepstake Overview"
        onClickButton={toggleModalIsOpen}
      />
      <Box
        sx={{
          borderWidth: 1,
          borderColor: 'grey.300',
          borderRadius: 2,
          p: '16px',
          mb: '40px',
        }}
      >
        <Grid container sx={{ rowGap: 5 }}>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              Sweepstake Name
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {sweepstake.name}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              Start Date
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {formatDateTime(sweepstake.startDate, 'P p')}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              End Date
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {formatDateTime(sweepstake.endDate, 'P p')}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              {`Number of Winners per Drawing`}
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {sweepstake.numLocationWinners ?? '-'}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              Sweepstake Description
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {sweepstake.description}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              Claim Period
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {sweepstake.numAffidavitDays}
            </Typography>
          </Grid>
          <Grid item md={4}>
            <Typography variant="body-2" color="text.secondary">
              Marketing Specialist Email
            </Typography>
            <Typography variant="body-1" color="text.primary">
              {sweepstake.marketingSpecialistEmail ?? '-'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <EditLocationSweepstakeDetailsModal
        sweepstake={sweepstake}
        updateIsActive={putLocationSweepstake.isPending}
        isModalOpen={isModalOpen}
        onSubmitSweepstake={(sweepstake) => {
          sweepstake.groupId
            ? putLocationSweepstakeGroup.mutate(sweepstake)
            : putLocationSweepstake.mutate(sweepstake)
        }}
        toggleModalIsOpen={toggleModalIsOpen}
      />
    </>
  )
}
